import { LatLng, PolygonOptions } from '@agm/core';
import { Component, NgZone, OnDestroy, OnInit } from '@angular/core';
import { Coordinate } from '../models/coordinate';
import { ClassificationsService } from '../services/classifications.service';
import { MapsService } from '../services/maps.service';
import { SpinnerService } from '../services/spinner.service';

declare const google: any;

@Component({
  selector: 'app-home',
  templateUrl: './home.component.html',
  styleUrls: ['./home.component.css']
})
export class HomeComponent implements OnInit, OnDestroy {
  _map: any;

  isPopupFieldOpen = false;
  showAreaDetail = false;
  originalMapCenter: Coordinate = { lat: -33.09470659406788, lng: -63.513642335937504 };
  popupCenter: Coordinate = { lat: -33.09470659406788, lng: -63.513642335937504 };
  originalMapZoom = 9;
  mapCenter: Coordinate;
  areaPolygons: Array<any> = new Array<any>();
  fieldPolygons: Array<any> = new Array<any>();

  cotagroLatLng: Coordinate = { lat: -32.819901, lng: -63.888606 };
  cotagroHa = 1418653;

  selectedPolygon: any;
  selectedPolygonOriginalCoords: Array<LatLng> = new Array<LatLng>();

  fieldPolygonOptions: PolygonOptions;
  areaPolygonOptions: PolygonOptions;

  isDrawing = false;
  drawingControlButton: HTMLButtonElement;
  drawingPath: any;
  drawingLine: any;
  drawingPreviewLine: any;
  drawingStartMarker: any;
  drawingListeners: Array<any> = [];

  constructor(private map: MapsService,
              private classificationService: ClassificationsService,
              private spinnerService: SpinnerService,
              private ngZone: NgZone) {
    this.map.getAreas().subscribe(result => {
      this.areaPolygons = result;
    });
    this.mapCenter = this.originalMapCenter;
  }

  ngOnInit() {
    this.fieldPolygonOptions = {
      editable: false,
      draggable: false,
      fillColor: '#000000',
      strokeColor: '#000000',
      fillOpacity: 0.5,
      strokeOpacity: 1,
      strokeWeight: 2,
      zIndex: 1000
    };

    this.areaPolygonOptions = {
      editable: false,
      draggable: false,
      fillColor: 'yellow',
      strokeColor: '#000000',
      fillOpacity: 0.1,
      strokeOpacity: 1,
      strokeWeight: 2,
      zIndex: 500
    };
  }

  ngOnDestroy() {
    this.stopDrawing();
  }

  /*  onMapClick(map) {
     this.setAllPolygonsNoEditable();
     this.isPopupFieldOpen = false;
   } */

  onMapReady(map) {
    this.initDrawingControl(map);
    this._map = map;

    this.drawFields();
  }

  // Google removed DrawingManager in Maps JS API v3.65, so polygons are drawn with a custom control.
  initDrawingControl(map: any) {
    const button = document.createElement('button');
    button.type = 'button';
    button.style.cssText = 'margin:10px;padding:0 12px;height:40px;border:none;border-radius:2px;'
      + 'box-shadow:rgba(0,0,0,0.3) 0 1px 4px -1px;background:#fff;color:#333;font:500 14px Roboto,Arial,sans-serif;cursor:pointer;';
    button.addEventListener('click', () => this.ngZone.run(() => this.isDrawing ? this.stopDrawing() : this.startDrawing()));
    this.drawingControlButton = button;
    this.updateDrawingControl();

    map.controls[google.maps.ControlPosition.TOP_CENTER].push(button);
  }

  startDrawing() {
    this.hideOptionsOnAreaClick(false);
    this.isDrawing = true;
    this.setFieldPolygonsClickable(false);
    this._map.setOptions({ draggableCursor: 'crosshair', disableDoubleClickZoom: true });

    const lineOptions = {
      map: this._map,
      clickable: false,
      strokeColor: this.fieldPolygonOptions.strokeColor,
      strokeWeight: this.fieldPolygonOptions.strokeWeight
    };
    this.drawingPath = new google.maps.MVCArray();
    this.drawingLine = new google.maps.Polyline({ ...lineOptions, path: this.drawingPath });
    this.drawingPreviewLine = new google.maps.Polyline({ ...lineOptions, strokeOpacity: 0.5 });

    this.drawingListeners = [
      google.maps.event.addListener(this._map, 'click', (event) => this.ngZone.run(() => this.addDrawingVertex(event.latLng))),
      google.maps.event.addListener(this._map, 'mousemove', (event) => this.updateDrawingPreview(event.latLng)),
      google.maps.event.addListener(this._map, 'dblclick', () => this.ngZone.run(() => this.finishDrawing()))
    ];
    document.addEventListener('keydown', this.onDrawingKeydown);

    this.updateDrawingControl();
  }

  addDrawingVertex(latLng: any) {
    this.drawingPath.push(latLng);

    if (this.drawingPath.getLength() === 1) {
      // Clicking the first vertex closes the polygon
      this.drawingStartMarker = new google.maps.Marker({
        map: this._map,
        position: latLng,
        cursor: 'pointer',
        title: 'Cerrar polígono',
        icon: {
          path: google.maps.SymbolPath.CIRCLE,
          scale: 6,
          fillColor: '#ffffff',
          fillOpacity: 1,
          strokeColor: this.fieldPolygonOptions.strokeColor,
          strokeWeight: 2
        }
      });
      this.drawingListeners.push(
        google.maps.event.addListener(this.drawingStartMarker, 'click', () => this.ngZone.run(() => this.finishDrawing()))
      );
    }
  }

  updateDrawingPreview(latLng: any) {
    const length = this.drawingPath.getLength();
    if (length === 0) {
      return;
    }

    const preview = [this.drawingPath.getAt(length - 1), latLng];
    if (length > 1) {
      preview.push(this.drawingPath.getAt(0));
    }
    this.drawingPreviewLine.setPath(preview);
  }

  finishDrawing() {
    // A double click also fires two map clicks on the same point, so drop consecutive duplicates
    const path = this.drawingPath.getArray().filter((latLng, i, arr) => i === 0 || !latLng.equals(arr[i - 1]));

    this.stopDrawing();

    if (path.length < 3) {
      return;
    }

    const polygon = new google.maps.Polygon({ ...this.fieldPolygonOptions, paths: path, map: this._map });
    this.onFieldPolygonDrawn(polygon);
  }

  stopDrawing() {
    if (!this.isDrawing) {
      return;
    }

    this.drawingListeners.forEach(listener => google.maps.event.removeListener(listener));
    this.drawingListeners = [];
    document.removeEventListener('keydown', this.onDrawingKeydown);

    this.drawingLine.setMap(null);
    this.drawingPreviewLine.setMap(null);
    if (this.drawingStartMarker) {
      this.drawingStartMarker.setMap(null);
      this.drawingStartMarker = undefined;
    }

    this._map.setOptions({ draggableCursor: null, disableDoubleClickZoom: false });
    this.setFieldPolygonsClickable(true);
    this.isDrawing = false;
    this.updateDrawingControl();
  }

  onDrawingKeydown = (event: KeyboardEvent) => {
    if (event.key === 'Escape') {
      this.ngZone.run(() => this.stopDrawing());
    }
  }

  onFieldPolygonDrawn(polygon: any) {
    this.mapCenter = this.getCenterOfPolygon(polygon.getPath().getArray());

    polygon.id = undefined;
    polygon.hectares = this.calculateHectares(polygon);

    this.fieldPolygons.push(polygon);

    google.maps.event.addListener(polygon, 'click', () => {
      this.selectedPolygonOriginalCoords = Object.assign([], polygon.getPath().getArray());
      this.onPolygonClick(polygon);
    });

    this.selectedPolygon = polygon;

    this.isPopupFieldOpen = true;
  }

  setFieldPolygonsClickable(clickable: boolean) {
    this.fieldPolygons.forEach(polygon => polygon.setOptions({ clickable }));
  }

  updateDrawingControl() {
    this.drawingControlButton.textContent = this.isDrawing ? 'Cancelar dibujo' : 'Dibujar campo';
    this.drawingControlButton.title = this.isDrawing
      ? 'Hacé clic en el primer vértice o doble clic para terminar. Esc para cancelar.'
      : 'Dibujar un nuevo campo';
  }

  showPercentage(areaId: number) {
    this.showAreaDetail = !this.showAreaDetail;
  }

  onAreaMarkerClick(infoWindow, map) {
    if (map.lastOpen != null) {
      map.lastOpen.close();
    }

    map.lastOpen = infoWindow;

    infoWindow.open();
  }

  onPolygonClick(polygon: any) {
    this.hideOptionsOnAreaClick(false);

    setTimeout(() => {
      this.selectedPolygon = polygon;
      this._map.setZoom(12);
      polygon.setEditable(true);

      this.mapCenter = this.getCenterOfPolygon(polygon.getPath().getArray());

      this.isPopupFieldOpen = true;
      this.showAreaDetail = false;
    }, 50);
  }

  setAllPolygonsNoEditable() {
    this.selectedPolygon = null;
    this.fieldPolygons.forEach((polygon) => {
      polygon.setEditable(false);
    });
  }

  getCenterOfPolygon(coordinates: LatLng[]) {
    const bounds = new google.maps.LatLngBounds();
    coordinates.forEach((latlng) => {
      bounds.extend(latlng);
    });

    const gCenter = bounds.getCenter();

    this.popupCenter.lat = gCenter.lat();
    this.popupCenter.lng = gCenter.lng();

    // return { lat: gCenter.lat(), lng: gCenter.lng() };
    const offsetX = 0.00; // move center one quarter map width left
    const offsetY = 0.3; // move center one quarter map height down

    const span = this._map.getBounds().toSpan(); // a latLng - # of deg map spans

    const newCenter = {
      lat: gCenter.lat() + span.lat() * offsetY,
      lng: gCenter.lng() + span.lng() * offsetX
    };

    return newCenter;
  }

  calculateHectares(poligono) {
    return (google.maps.geometry.spherical.computeArea(poligono.getPath()) / 10000).toFixed(2);
  }

  hideOptionsOnAreaClick(isPopupFieldOpen: boolean) {
    if (isPopupFieldOpen === false) {
      this.isPopupFieldOpen = false;
      this.showAreaDetail = false;
      this.selectedPolygon = undefined;
      this.setAllPolygonsNoEditable();
    }
  }

  closeFieldPopup(result: any) {
    // Remove drawed polygon
    if (result.cancelAction && this.selectedPolygon.id === undefined) {
      this.fieldPolygons.filter(x => x.id === undefined)[0].setMap(null);
      this.fieldPolygons = this.fieldPolygons.filter(x => x.id !== undefined);
    }

    // Revert polygon to original shape(discard changes)
    if (result.cancelAction && this.selectedPolygon.id !== undefined) {
      this.fieldPolygons.filter(x => x.id === this.selectedPolygon.id)[0].setPath(Object.assign([], this.selectedPolygonOriginalCoords));
      this.selectedPolygonOriginalCoords = new Array<LatLng>();
    }

    if (!result.cancelAction && !result.closePopup) {
      this.classificationService.get(result.classificationId).subscribe(data => {
        this.fieldPolygons.filter(x => x.id === this.selectedPolygon.id)[0].setOptions({ fillColor: data.color, strokeColor: data.color });
        this.hideOptionsOnAreaClick(!result.closePopup);
      });
    } else {
      this.hideOptionsOnAreaClick(!result.closePopup);
    }
  }

  drawAreas() {
    this.map.getAreas().subscribe((fields: Array<any>) => {

      fields.forEach(field => {
        const latlngArray = field.coordinates.map((latlng) => {
          return new google.maps.LatLng(latlng.lat, latlng.lng);
        });

        const polygon = new google.maps.Polygon({
          id: field.id,
          paths: latlngArray,
          strokeColor: this.areaPolygonOptions.strokeColor,
          strokeOpacity: this.areaPolygonOptions.strokeOpacity,
          strokeWeight: this.areaPolygonOptions.strokeWeight,
          fillColor: this.areaPolygonOptions.fillColor,
          fillOpacity: this.areaPolygonOptions.fillOpacity,
          editable: this.areaPolygonOptions.editable,
          draggable: this.areaPolygonOptions.draggable,
        });

        polygon.setMap(this._map);
      });

      this.drawFields();
    });
  }

  drawFields() {
    this.spinnerService.setTrue();
    this.map.getFields().subscribe((fields: Array<any>) => {

      fields.forEach(field => {
        const latlngArray = field.coordinates.map((latlng) => {
          return new google.maps.LatLng(latlng.lat, latlng.lng);
        });

        const polygon = new google.maps.Polygon({
          id: field.id,
          paths: latlngArray,
          strokeColor: field.color,
          strokeOpacity: this.fieldPolygonOptions.strokeOpacity,
          strokeWeight: this.fieldPolygonOptions.strokeWeight,
          fillColor: field.color,
          fillOpacity: this.fieldPolygonOptions.fillOpacity,
          editable: this.fieldPolygonOptions.editable,
          draggable: this.fieldPolygonOptions.draggable,
          zIndex: this.fieldPolygonOptions.zIndex
        });

        polygon.setMap(this._map);

        google.maps.event.addListener(polygon, 'click', () => {
          this.selectedPolygonOriginalCoords = Object.assign([], polygon.getPath().getArray());
          this.onPolygonClick(polygon);
        });

        this.fieldPolygons.push(polygon);
      });

      this.spinnerService.setFalse();
    });
  }

  buildAreaTitle(name: string, hectare: number) {
    return `Sucursal: ${name} - Cantidad total de hectáreas: ${hectare}`;
  }
}
